import React from "react"

import moment from "moment-timezone"

import { PrimaryText } from "@front-shared/elements/primaryText.element"

import type { IBulkResultsInsightsModel } from "../../../types/bulkResultsInsights.types"

export const BulkResultsRunSummaryComponent: React.FunctionComponent<
    IBulkResultsRunSummaryComponentProps
> = props => {
    const remainingCount = Math.max(
        0,
        props.model.totalPatients - props.model.completedCount,
    )

    const progressPercent = props.model.totalPatients
        ? Math.round(
              (props.model.completedCount / props.model.totalPatients) * 100,
          )
        : 0

    const lastResultLabel = props.model.finishedAt
        ? moment(props.model.finishedAt).format("h:mm A")
        : null

    const creditsLabel = `${props.model.creditsUsed} credit${
        props.model.creditsUsed === 1 ? "" : "s"
    } used`

    const fullCoverageClause = `${props.model.fullCoverageCount} patient${
        props.model.fullCoverageCount === 1 ? "" : "s"
    } came back with a full coverage picture${
        props.model.inDeductibleCount
            ? `, ${props.model.inDeductibleCount} of them still inside their deductible`
            : ""
    }`

    return (
        <div className={props.className}>
            <PrimaryText typography="h4" className="mb-[8px]">
                {props.isRunning ? "Running checks…" : "Run complete."}
            </PrimaryText>

            <PrimaryText typography="subtitle" className="opacity-60">
                {props.isRunning
                    ? `${props.model.completedCount} of ${props.model.totalPatients} complete · ${remainingCount} still running · ${creditsLabel}`
                    : `${props.model.completedCount} patient${
                          props.model.completedCount === 1 ? "" : "s"
                      } · ${creditsLabel}${
                          lastResultLabel
                              ? ` · finished ${lastResultLabel}`
                              : ""
                      }`}
            </PrimaryText>

            {props.isRunning ? (
                <div
                    role="progressbar"
                    aria-label="Coverage check progress"
                    aria-valuemin={0}
                    aria-valuemax={props.model.totalPatients}
                    aria-valuenow={props.model.completedCount}
                    className="
                        mt-[12px] h-[6px] max-w-[760px] overflow-hidden rounded-full
                        bg-brand-warmLight2
                    "
                >
                    <div
                        className="h-full rounded-full bg-brand-primary transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            ) : null}

            <PrimaryText className="mt-[16px] max-w-[760px]">
                {props.isRunning
                    ? `Every section below updates as results come in. So far, ${fullCoverageClause}.`
                    : `${fullCoverageClause}. ${props.model.secondPassCount} need a second pass — each outcome below carries the one action that clears it.`}
            </PrimaryText>
        </div>
    )
}

interface IBulkResultsRunSummaryComponentProps {
    className?: string
    isRunning: boolean
    model: IBulkResultsInsightsModel
}
